/*  MUI  */
import {
    AssignmentTurnedIn,
    NoteAltOutlined,
    RestorePage
} from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core/DateFormat';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Notify, SessionHelper } from 'application/Shared/Helpers';
import { Note } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import { Application } from 'objects/Application';
import NotesModal from 'objects/Application/NotesModal';

/*  React  */
import React, { Component } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/*  Components  */
import Cards from './Components/Cards';

/*  Constants  */
const endpoints = {
    client: '/note',
    completed: '/completedNote'
};

/*  State  */
const state = () => ({
    cardParams: {},
    loaded: false,
    modal: false,
    notes: {
        client: [],
        community: [],
        partner: []
    },
    partner: false,
    rerender: false,
    tableParams: {},
    toggles: {}
});

const RenderAppCell = ({ row }) => {
    const { t } = useTranslation();
    const { application } = row;
    if (!application) {
        return <TableCell />;
    }

    return (
        <TableCell>
            <Application
                data={application}
                fromPage={t('Common.notes')}
                nameHasLink
            >
                <ApplicationNameLinkCell withCategory />
            </Application>
        </TableCell>
    );
};

class AppNotes extends Component {
    constructor(props) {
        super(props);
        this.state = state();

        const clientId = props?.match?.params?.clientId;
        /* requests */
        this.Note = new Note(clientId);
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate() {
        const { reload } = this.props;
        if (reload) {
            this.reload();
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.unload();
    }

    load = async () => {
        const { session, application, match, report, tenants } = this.props;
        const { partnerId } = match.params;

        // If no partnerId in the URL, use session tenant as fallback.
        const sessionHelper = new SessionHelper(session);
        const partnerTenantInfo = tenants[partnerId];
        const partnerTenant = partnerTenantInfo?.tenant;
        const tenant = partnerTenant || sessionHelper.getTenant();
        // See if the tenant is a Partner.
        const partner = tenant && tenant.partner;

        await this.loadNotes();

        const tableParams = {
            applicationId: '*',
            excludeArchived: true,
            excludeCompleted: true,
            includeAll: true
        };
        let toggles = [];

        const cardParams = { applicationId: '*', includeCompleted: true };

        if (application) {
            tableParams.applicationId = application.id;
            cardParams.applicationId = application.id;
            toggles = [
                {
                    defaultEnabled: false,
                    key: 'completed',
                    parameter: { field: 'includeCompleted', value: true },
                    toggleText: 'Show Completed Notes'
                }
            ];
        } else {
            tableParams.includeApp = true;
        }

        if (report) {
            tableParams.includeCompleted = true;
            tableParams.excludeCompleted = false;
            tableParams.includeOnlyThisTenant = true;
        }

        if (!this.unmounted) {
            this.setState({
                cardParams,
                loaded: true,
                partner,
                tableParams,
                toggles
            });
        }
    };

    reload = () => {
        this.setState(state(), () => this.load());
    };

    rerenderFn = () => {
        this.setState({ rerender: true }, () => {
            this.setState({ rerender: false });
        });
    };

    unload = () => {
        this.Note.cancel();
    };

    loadNotes = async () => {
        const { application } = this.props;

        const payload = { applicationId: '*', excludeCompleted: true };
        if (application) {
            payload.applicationId = application.id;
        }

        let client;
        let partner;
        let community;

        try {
            client = await this.Note.getClient({
                ...payload,
                excludeArchived: true
            });
        } catch (e) {
            return;
        }

        try {
            partner = await this.Note.getPartner({
                ...payload,
                archived: false
            });
        } catch (e) {
            return;
        }

        try {
            community = await this.Note.getCommunity({
                ...payload,
                approved: true,
                includeImported: true
            });
        } catch (e) {
            return;
        }

        if (!this.unmounted) {
            this.setState({ notes: { client, community, partner } });
        }
    };

    reloadNotes = () => {
        this.loadNotes();
    };

    complete = async (row) => {
        const { match } = this.props;
        debug.i('Completing App Note', row);
        const { id, partner, community } = row;

        let type = 'client';
        if (partner) {
            type = 'partner';
        }
        if (community) {
            type = 'community';
        }

        try {
            await axios.post(endpoints.completed, {
                noteId: id,
                platformTenantId: match?.params?.clientId,
                type
            });
            Notify.i('Note has been marked as completed.');
            this.reloadNotes();
            this.rerenderFn();
        } catch (e) {
            debug.e('Unable to mark note as completed.', e);
            Notify.e('Unable to mark note as completed.');
        }
    };

    restore = async (row) => {
        const { id } = row;
        debug.i('Restoring Completed Note', row);

        try {
            await axios.delete(`${endpoints.completed}/${id}`);
            Notify.i('Note has been restored.');
            this.rerenderFn();
        } catch (e) {
            debug.e('Unable to restore note.', e);
            Notify.e('Unable to restore note.');
        }
    };

    renderHeaders = () => {
        const { t, application, report } = this.props;

        const headers = [];
        if (!application) {
            headers.push({
                field: 'application.name',
                renderHeader: t('Common.application'),
                sortableKey: 'application.name',
                width: '25%'
            });
        }

        headers.push(
            {
                field: 'type',
                renderHeader: t('Common.type'),
                width: `${!application ? '10%' : '25%'}`
            },
            {
                field: 'message',
                renderHeader: t('Common.note/recommendation'),
                width: `${!application ? '45%' : '55%'}`
            }
        );

        if (!report) {
            headers.push({
                align: 'right',
                field: 'actions',
                renderHeader: t('Common.actions'),
                width: '20%'
            });
        } else {
            headers.push({
                field: 'dateCompleted',
                renderHeader: t('Notes.status'),
                width: '20%'
            });
        }

        return headers;
    };

    renderActions = (row) => {
        const { t } = this.props;
        const { completed, dateCompleted } = row;

        if (!completed) {
            return (
                <Button
                    data-trackid="notesPageActionsMarkAsComplete"
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={() => this.complete(row)}
                >
                    <AssignmentTurnedIn style={{ marginRight: '4px' }} />
                    {t('Notes.mark_as_complete')}
                </Button>
            );
        }

        return (
            <>
                <Button
                    data-trackid="notesPageActionsRestoreNote"
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => this.restore(row)}
                >
                    <RestorePage style={{ marginRight: '4px' }} />
                    {t('Common.restore')}
                </Button>
                <Box>
                    {t('Common.completed')} <DateFormat date={dateCompleted} />
                </Box>
            </>
        );
    };

    renderStatusCell = (row) => {
        const { t } = this.props;
        const { completed, dateCompleted } = row;

        if (!completed) {
            return <span>{t('Common.active')}</span>;
        }

        return (
            <span>
                {t('Common.completed')} <DateFormat date={dateCompleted} />
            </span>
        );
    };

    renderRow = (row) => {
        const { application, report } = this.props;
        const { type, message } = row;

        return (
            <>
                {!application && <RenderAppCell row={row} />}
                <TableCell>{type}</TableCell>
                <TableCell>{message}</TableCell>
                <TableCell align="right">
                    {report
                        ? this.renderStatusCell(row)
                        : this.renderActions(row)}
                </TableCell>
            </>
        );
    };

    renderTable = () => {
        const { report } = this.props;
        const { tableParams, rerender, toggles } = this.state;
        const searchFields = ['id', 'type', 'message'];
        const rowsApi = endpoints.client;
        const tableHeaders = this.renderHeaders();

        return (
            <ManagedTable
                headers={tableHeaders}
                parameters={tableParams}
                renderBody={this.renderRow}
                renderBodyKey="id"
                renderFooter={!report}
                rerender={rerender}
                rowsApi={rowsApi}
                rowsApiPreload
                rowsPerPage={report ? -1 : 10}
                searchEnabled={!report}
                searchFields={searchFields}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="completed"
                toggles={toggles}
            />
        );
    };

    renderManageButton = () => {
        const { t } = this.props;

        return (
            <Box
                display="flex"
                justifyContent="flex-end"
                width="100%"
                mr="8px"
                mt="6px"
            >
                <Button
                    data-trackid="notesPageManageNotesBtn"
                    color="primary"
                    variant="contained"
                    onClick={() => this.setState({ modal: true })}
                >
                    <NoteAltOutlined style={{ marginRight: '4px' }} />
                    {t('Notes.manage')}
                </Button>
            </Box>
        );
    };

    renderModal = () => {
        const { application } = this.props;
        const { modal, partner, notes } = this.state;

        if (!modal) {
            return null;
        }

        return (
            <NotesModal
                application={application}
                clientNotes={notes.client}
                partnerNotes={notes.partner}
                communityNotes={notes.community}
                partner={partner}
                open
                modalOnly
                onClose={() => this.setState({ modal: false })}
                onSave={(reload) => {
                    if (reload) {
                        this.setState({ modal: false }, () => {
                            this.reloadNotes();
                            this.reload();
                        });
                    }
                }}
            />
        );
    };

    renderCards = () => {
        const { report, noCards } = this.props;
        const { cardParams } = this.state;

        if (noCards) {
            return null;
        }

        return (
            <Grid item xs={12}>
                <Cards report={report} params={cardParams} />
            </Grid>
        );
    };

    render() {
        const { t, showTitle, report } = this.props;
        const { loaded } = this.state;

        if (!loaded) {
            return (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Paper elevation={0} variant="outlined">
                            <Grid item xs={12}>
                                {showTitle && (
                                    <Typography variant="h5">
                                        {t('Common.notes_and_recommendations')}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid
                                display="flex"
                                justifyContent="center"
                                marginY={4}
                            >
                                <CircularProgress />
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            );
        }

        return (
            <Grid container spacing={3}>
                {this.renderCards()}
                <Grid item xs={12}>
                    <Paper elevation={0} variant="outlined">
                        <Grid item xs={12}>
                            {showTitle && (
                                <Typography variant="h5">
                                    {t('Common.notes_and_recommendations')}
                                </Typography>
                            )}
                            {!report && this.renderManageButton()}
                            {!report && this.renderModal()}
                            {this.renderTable()}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        );
    }
}

export default withRouter(withTranslation()(AppNotes));
